import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format } from "date-fns";
import { CalendarIcon, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { submitQuote } from "@/lib/api";

const schema = z.object({
  name: z.string().min(2, "Please enter your full name"),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().min(7, "Please enter a contactable phone number"),
  postcode: z.string().max(20).optional().or(z.literal("")),
  project_type: z.enum(["bathroom", "kitchen", "both", "plumbing_heating"], {
    required_error: "Please select a project type",
  }),
  scope: z.enum(
    ["design_only", "installation_only", "design_and_installation", "repair_maintenance"],
    { required_error: "Please tell us the scope" }
  ),
  budget: z.enum(
    ["under_5k", "5k_10k", "10k_20k", "20k_40k", "40k_plus", "not_sure"],
    { required_error: "Please indicate a rough budget" }
  ),
  preferred_date: z.date().optional(),
  message: z
    .string()
    .min(10, "Please share a few more details so we can quote accurately")
    .max(4000),
});

const projectTypes = [
  { v: "bathroom", l: "Bathroom" },
  { v: "kitchen", l: "Kitchen" },
  { v: "both", l: "Bathroom + Kitchen" },
  { v: "plumbing_heating", l: "Plumbing or Heating only" },
];
const scopes = [
  { v: "design_only", l: "Design only" },
  { v: "installation_only", l: "Installation only" },
  { v: "design_and_installation", l: "Design + Installation" },
  { v: "repair_maintenance", l: "Repair or maintenance" },
];
const budgets = [
  { v: "under_5k", l: "Under £5,000" },
  { v: "5k_10k", l: "£5,000 – £10,000" },
  { v: "10k_20k", l: "£10,000 – £20,000" },
  { v: "20k_40k", l: "£20,000 – £40,000" },
  { v: "40k_plus", l: "£40,000+" },
  { v: "not_sure", l: "Not sure yet" },
];

export default function QuoteSection() {
  const [submitted, setSubmitted] = useState(false);
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      postcode: "",
      project_type: undefined,
      scope: undefined,
      budget: undefined,
      preferred_date: undefined,
      message: "",
    },
  });

  const onSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        postcode: values.postcode || null,
        preferred_date: values.preferred_date
          ? format(values.preferred_date, "yyyy-MM-dd")
          : null,
      };
      const res = await submitQuote(payload);
      setSubmitted(true);
      toast.success("Quotation request sent", {
        description: `Thanks ${res.name} — we&apos;ll be in touch within 1 working day.`,
      });
      form.reset();
    } catch (e) {
      toast.error("Something went wrong", {
        description:
          e?.response?.data?.detail ||
          "Please try again, or call us directly if the issue persists.",
      });
    }
  };

  const onInvalid = () => {
    toast.error("Please complete the highlighted fields.");
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="py-24 lg:py-32 bg-[#1C2A39] text-[#F9F8F6] c4-grain relative"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <span className="uppercase text-xs tracking-[0.3em] text-[#E9D28B] font-semibold">
            Request a Quotation
          </span>
          <h2
            data-testid="quote-title"
            className="mt-4 font-heading text-3xl lg:text-5xl text-white font-medium tracking-tight"
          >
            Tell us about your project.
          </h2>
          <p className="mt-6 text-base text-[#C7CCD3] leading-relaxed max-w-md">
            Share a few details and we&apos;ll come back within one working day
            with next steps. All quotations are free, no-obligation and fully
            itemised.
          </p>

          <ul className="mt-10 space-y-4">
            {[
              "Free, no-obligation site survey",
              "Itemised, fixed-price quotations",
              "Direct line to your project lead",
              "12-month workmanship guarantee",
            ].map((p) => (
              <li
                key={p}
                className="flex items-start gap-3 text-sm text-[#E8E5DD]"
              >
                <CheckCircle2
                  size={18}
                  strokeWidth={1.5}
                  className="text-[#E9D28B] mt-0.5 shrink-0"
                />
                {p}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <div className="bg-[#F9F8F6] text-[#1A1A1A] p-7 lg:p-10 border border-[#2A3D52]">
            {submitted ? (
              <div
                data-testid="quote-success-state"
                className="py-10 text-center"
              >
                <div className="mx-auto w-14 h-14 grid place-items-center bg-[#C9A24A]/10 border border-[#C9A24A]/30">
                  <CheckCircle2
                    size={26}
                    strokeWidth={1.5}
                    className="text-[#C9A24A]"
                  />
                </div>
                <h3 className="mt-6 font-heading text-2xl text-[#1C2A39] font-medium">
                  Request received.
                </h3>
                <p className="mt-3 text-sm text-[#5C6670] max-w-md mx-auto leading-relaxed">
                  Thanks for getting in touch. A member of the C4 team will
                  reply within one working day to confirm next steps.
                </p>
                <Button
                  data-testid="quote-success-reset"
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="mt-7 rounded-sm border-[#1C2A39] text-[#1C2A39] hover:bg-[#1C2A39] hover:text-white h-11 px-6"
                >
                  Send another
                </Button>
              </div>
            ) : (
              <Form {...form}>
                <form
                  data-testid="quote-form"
                  onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                  className="space-y-6"
                  noValidate
                >
                  {Object.keys(form.formState.errors).length > 0 && (
                    <div
                      role="alert"
                      data-testid="quote-form-error-summary"
                      className="border border-[#C9A24A]/40 bg-[#C9A24A]/5 text-[#B0893A] px-4 py-3 text-sm font-heading"
                    >
                      Please complete the highlighted fields below before sending.
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
                            Full name
                          </FormLabel>
                          <FormControl>
                            <Input
                              data-testid="quote-input-name"
                              placeholder="Jane Smith"
                              className="rounded-sm border-[#E8E5DD] bg-white h-11 focus-visible:ring-[#C9A24A]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
                            Email
                          </FormLabel>
                          <FormControl>
                            <Input
                              data-testid="quote-input-email"
                              type="email"
                              placeholder="you@example.co.uk"
                              className="rounded-sm border-[#E8E5DD] bg-white h-11 focus-visible:ring-[#C9A24A]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
                            Phone
                          </FormLabel>
                          <FormControl>
                            <Input
                              data-testid="quote-input-phone"
                              placeholder="07000 000 000"
                              className="rounded-sm border-[#E8E5DD] bg-white h-11 focus-visible:ring-[#C9A24A]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="postcode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
                            Postcode <span className="text-[#5C6670] normal-case tracking-normal">(optional)</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              data-testid="quote-input-postcode"
                              placeholder="SW18 4XX"
                              className="rounded-sm border-[#E8E5DD] bg-white h-11 focus-visible:ring-[#C9A24A]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="project_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
                            Project type
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                data-testid="quote-select-project-type"
                                className="rounded-sm border-[#E8E5DD] bg-white h-11 focus:ring-[#C9A24A]"
                              >
                                <SelectValue placeholder="Choose project" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {projectTypes.map((o) => (
                                <SelectItem
                                  key={o.v}
                                  value={o.v}
                                  data-testid={`quote-project-option-${o.v}`}
                                >
                                  {o.l}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="scope"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
                            Scope
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                data-testid="quote-select-scope"
                                className="rounded-sm border-[#E8E5DD] bg-white h-11 focus:ring-[#C9A24A]"
                              >
                                <SelectValue placeholder="Choose scope" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {scopes.map((o) => (
                                <SelectItem
                                  key={o.v}
                                  value={o.v}
                                  data-testid={`quote-scope-option-${o.v}`}
                                >
                                  {o.l}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="budget"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
                            Approx. budget
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                data-testid="quote-select-budget"
                                className="rounded-sm border-[#E8E5DD] bg-white h-11 focus:ring-[#C9A24A]"
                              >
                                <SelectValue placeholder="Choose range" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {budgets.map((o) => (
                                <SelectItem
                                  key={o.v}
                                  value={o.v}
                                  data-testid={`quote-budget-option-${o.v}`}
                                >
                                  {o.l}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="preferred_date"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
                            Preferred contact date{" "}
                            <span className="text-[#5C6670] normal-case tracking-normal">
                              (optional)
                            </span>
                          </FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  type="button"
                                  variant="outline"
                                  data-testid="quote-date-trigger"
                                  className={cn(
                                    "h-11 justify-start font-normal rounded-sm border-[#E8E5DD] bg-white text-[#1C2A39] hover:bg-white hover:border-[#C9A24A]",
                                    !field.value && "text-[#5C6670]"
                                  )}
                                >
                                  <CalendarIcon
                                    className="mr-2 h-4 w-4 text-[#C9A24A]"
                                    strokeWidth={1.5}
                                  />
                                  {field.value
                                    ? format(field.value, "PPP")
                                    : "Pick a date"}
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0 bg-white border-[#E8E5DD]"
                              align="start"
                              data-testid="quote-date-popover"
                            >
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date) =>
                                  date < new Date(new Date().setHours(0, 0, 0, 0))
                                }
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-heading text-xs uppercase tracking-[0.18em] text-[#1C2A39]">
                          Tell us about the project
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            data-testid="quote-input-message"
                            rows={5}
                            placeholder="A few details about the space, your goals, any timing constraints…"
                            className="rounded-sm border-[#E8E5DD] bg-white focus-visible:ring-[#C9A24A] resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                    <p className="text-xs text-[#5C6670] max-w-xs">
                      We&apos;ll only use your details to reply about this
                      enquiry. No marketing.
                    </p>
                    <Button
                      type="submit"
                      data-testid="quote-submit-button"
                      disabled={form.formState.isSubmitting}
                      className="bg-[#C9A24A] hover:bg-[#B0893A] text-white rounded-sm h-12 px-8 font-heading font-semibold tracking-wide"
                    >
                      {form.formState.isSubmitting ? "Sending..." : (
                        <>
                          Send request
                          <Send size={16} strokeWidth={1.75} className="ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
